import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Calculator, Scale, ScrollText } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';

/**
 * Bottom tab bar — mobile only (hidden at >=1024px per rebuild directive).
 *
 * Four tabs, all driving real routing (not scroll):
 *   Home    → /
 *   Zakat   → /zakat/calculator
 *   Faraid  → /inheritance
 *   Will    → /will
 *
 * Active state is derived from `location.pathname` so the tab stays in
 * sync when the user uses the browser back button or arrives via a deep
 * link. Fixed 56px + env(safe-area-inset-bottom).
 */
const Item = ({ active, onClick, Icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-current={active ? 'page' : undefined}
    className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 h-full transition-colors ${
      active ? 'text-gold-400' : 'text-[#8B8172] hover:text-[#0D1520]'
    }`}
  >
    <Icon className="w-[20px] h-[20px]" />
    {label && (
      <span className={`text-[11px] leading-none ${active ? 'font-semibold' : 'font-medium'}`}>{label}</span>
    )}
  </button>
);

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useZakatGlobal();
  const path = location.pathname;

  // Signal to global CSS that we should reserve space for a fixed bottom
  // nav — enables safe-area-aware bottom padding on body.
  useEffect(() => {
    document.body.classList.add('gd-has-bottom-nav');
    return () => document.body.classList.remove('gd-has-bottom-nav');
  }, []);

  const isHome = path === '/' || path === '';
  const isZakat = path.startsWith('/zakat') || path.startsWith('/business');
  const isInh   = path.startsWith('/inheritance');
  const isWill  = path.startsWith('/will');

  return (
    <nav
      aria-label="Primary"
      data-testid="mobile-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-[#EDF5F1] backdrop-blur-md border-t border-[#CBD2D9]"
      style={{ height: 'calc(56px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="max-w-3xl mx-auto flex items-stretch h-[56px]">
        <Item active={isHome}  onClick={() => navigate('/')}                 Icon={Home}       label={''}                                testid="bottom-nav-home" />
        <Item active={isZakat} onClick={() => navigate('/zakat/calculator')} Icon={Calculator} label={t('nav.zakatTab') || 'Zakat'}       testid="bottom-nav-zakat" />
        <Item active={isInh}   onClick={() => navigate('/inheritance')}      Icon={Scale}      label={t('nav.faraidTab') || 'Faraid'}    testid="bottom-nav-inheritance" />
        <Item active={isWill}  onClick={() => navigate('/will')}             Icon={ScrollText} label={t('nav.willTab') || 'Will'}        testid="bottom-nav-will" />
      </div>
    </nav>
  );
};

export default MobileBottomNav;
